/** Manages the UI state and triggers for adding a new task. */
import { useState, useEffect, useCallback } from 'react';
import { subscribeToAuthChanges } from '@/services/authService';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import useNewTaskLogic from '@/features/tasks/hooks/mutations/use-task-new-logic';
import {
  saveNewTaskWithOptimisticPosition,
  openNewTaskInputSession,
} from '@/features/tasks/hooks/mutations/use-task-new-state.helpers';

/**
 * @param {{ onAddTask: Function, sessionRef?: { current: number } }} params
 * @returns {{
 *   newTaskPosition: object|null,
 *   openNewTaskInput: (newPos: object, key?: string|null) => void,
 *   cancel: () => void,
 *   handleSaveNewTask: (taskData: object) => Promise<void>,
 *   triggeringKey: string|null,
 * }}
 */
const useNewTask = ({ onAddTask, sessionRef }) => {
  const [position, setPosition] = useState(null);
  const [triggeringKey, setTriggeringKey] = useState(null);
  const [user, setUser] = useState(null);
  const { allTasks } = useTasksContext();
  const { augmentNewTaskWithDefaults } = useNewTaskLogic();

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((u) => setUser(u));
    return () => unsubscribe();
  }, []);

  const cancel = useCallback(() => setPosition(null), []);

  const handleSaveNewTask = async (taskData) =>
    saveNewTaskWithOptimisticPosition({
      taskData,
      position,
      setPosition,
      allTasks,
      user,
      augmentNewTaskWithDefaults,
      cancel,
      onAddTask,
    });

  const openNewTaskInput = (newPos, key = null) =>
    openNewTaskInputSession({
      newPos,
      key,
      sessionRef,
      setPosition,
      setTriggeringKey,
    });

  useEffect(() => {
    if (!position) setTriggeringKey(null);
  }, [position]);

  return {
    newTaskPosition: position,
    openNewTaskInput,
    cancel,
    handleSaveNewTask,
    triggeringKey,
  };
};

export default useNewTask;
